// 🏡 The painted homes (PORTED_HOME_HANDOFF_2026_09_25 §6 + §10). Pure data and
// math, no React: the painted-home stage (components/ChatGame/PortedHome.js)
// and the light room's choice scene (lightRoomEngine) both read it, so the
// canvas zoom through the chosen door lands on exactly the crop the stage
// then shows (coverPan below).
//
// Keys match the backend's src/ChatGame/portedHome.js HOMES (the resident is
// told about exactly these); portedHomesParity.test.js requires that module.
// Geometry is % of the painting (every take is the same 2400x1001 crop of the
// round-1 originals, the handoff's 1600x667 numbers scale 1:1).

export const HOME_ART_ASPECT = 2400 / 1001;

// Hosted like the room art (never public/, rule 12), uploaded 09-25 by
// justsomeguy-backend/tools/upload-ported-home-art.js (7 files, 2400x1001,
// HEAD-checked 200 image/jpeg). The prefix is VERSIONED: the objects carry a
// year-long immutable cache, so a retake goes to v2, never over v1.
export const HOME_ART_BASE = 'https://spaceexplorer-content-files.s3.us-east-1.amazonaws.com/banner-images/ported-homes/v1';
const art = (key, kinds) => Object.fromEntries(kinds.map((k) => [k, `${HOME_ART_BASE}/${key}-${k}.jpg`]));
// 🚪 W3: each home's painted OUTSIDE (the front door + the /explore porch).
// Round 2 (09-26) painted the glasshouse's + the loft's; the attic's is round
// 1. Same 2400x1001 crop, so these % numbers are exact (measured on a grid
// over the web crop). `porch` = the one printed line strangers read.
const outside = (key, o) => ({ url: `${HOME_ART_BASE}/${key}-outside-dusk.jpg`, ...o });

export const PORTED_HOMES = {
  attic: {
    key: 'attic',
    name: "the stargazer's attic",
    art: art('attic', ['day', 'night', 'storm']),
    outside: outside('attic', { door: { x: 55.2, y: 73, w: 5.5, h: 17 }, knocker: { x: 55.2, y: 70 }, lit: { x: 50, y: 25 }, porch: 'a crooked house with a very large telescope' }),
    win: { x: 42.0, y: 20.1, w: 16.6, h: 39.0, radius: '50%' },
    sky: { tint: true, clouds: true, stars: true },
    // W2d (§10 table): the moon's place + size in the painting, how far down
    // the window stars may sit (% of the window), the painted cork board, the
    // 📓 hotspot (the armchair's arm; never the desk)
    moon: { x: 54.4, y: 29.8, size: 2.6 },
    starsBelow: 100,
    cork: { x: 86.8, y: 42.8, w: 10.6, h: 33, rot: 0 },
    notebook: { x: 35.2, y: 64.5 },
    spots: {
      chair: { x: 27.6, y: 64, place: 'the armchair' },
      desk: { x: 50.2, y: 76, place: 'the desk' },
      rest: { x: 70.6, y: 56, place: 'the hammock' },
      music: { x: 50.2, y: 76, place: 'the desk' },
    },
    lamps: [{ x: 32, y: 66, w: 26, h: 52 }, { x: 45.8, y: 56, w: 9, h: 20 }],
    ground: { day: [36, 25, 19], night: [18, 15, 31] },
  },
  glasshouse: {
    key: 'glasshouse',
    name: 'the glasshouse',
    art: art('glasshouse', ['day', 'night', 'storm']),
    outside: outside('glasshouse', { door: { x: 61.2, y: 66, w: 6, h: 32 }, knocker: { x: 61.1, y: 59.4 }, lit: { x: 52, y: 45 }, porch: 'a little glass house with a monstera for a roof' }),
    win: { x: 0, y: 0, w: 100, h: 60, radius: '0' },
    sky: { tint: false, clouds: false, stars: false },   // the whole wall is glass
    moon: { x: 81.5, y: 8.5, size: 2.3 },
    starsBelow: 45,
    cork: { x: 84.2, y: 40.2, w: 12.6, h: 30, rot: -4 },
    notebook: { x: 12.4, y: 78.5 },   // the side table by the teacup
    spots: {
      chair: { x: 18.6, y: 62, place: 'the velvet chair' },
      desk: { x: 50, y: 79, place: 'the potting bench' },
      rest: { x: 83, y: 80, place: 'the daybed' },
      music: { x: 74.4, y: 63, place: 'the record player' },
    },
    lamps: [{ x: 58, y: 55, w: 14, h: 30 }],
    ground: { day: [22, 30, 24], night: [14, 17, 30] },
  },
  loft: {
    key: 'loft',
    name: 'the loft over a record shop',
    art: art('loft', ['day', 'night', 'storm']),
    outside: outside('loft', { door: { x: 57.5, y: 70, w: 6, h: 28 }, knocker: { x: 57.7, y: 66 }, lit: { x: 50, y: 22 }, porch: 'a loft over a record shop, the window always lit' }),
    win: { x: 35.0, y: 12.0, w: 31.4, h: 48.6, radius: '50% 50% 0 0 / 24% 24% 0 0' },
    sky: { tint: true, clouds: true, stars: true },
    moon: { x: 53, y: 33, size: 2.6 },
    starsBelow: 72,
    cork: { x: 81.9, y: 13.6, w: 14, h: 48.4, rot: 0 },
    notebook: { x: 28.5, y: 68 },     // the couch arm
    spots: {
      chair: { x: 15, y: 67, place: 'the couch' },
      desk: { x: 50, y: 77, place: 'the window shelf' },
      rest: { x: 11, y: 71, place: 'the couch' },
      music: { x: 50, y: 77, place: 'the turntable' },
    },
    lamps: [{ x: 52, y: 62, w: 20, h: 40 }, { x: 73.7, y: 57, w: 10, h: 22 }],
    ground: { day: [30, 24, 22], night: [15, 14, 30] },
  },
};

export const homeFor = (key) => (key && PORTED_HOMES[key]) || null;
export const LIBRARY_KEYS = ['attic', 'glasshouse', 'loft'];

// Status → a spot in the room (§6 Presence). His status line is his own
// declared activity; the spot is only where the light sits.
export function spotKey(status) {
  const s = String(status || '').toLowerCase();
  if (/\b(read|book|tea|knit)/.test(s)) return 'chair';
  if (/\b(writ|draft|desk|note|sketch|work)/.test(s)) return 'desk';
  if (/\b(hammock|nap|daybed|couch|rest|think)/.test(s)) return 'rest';
  if (/\b(record|music|song|listen)/.test(s)) return 'music';
  return 'desk';
}
export const spotFor = (home, status) => (home ? home.spots[spotKey(status)] || home.spots.desk : null);

// Cover-fit the painting to a box of W x H, then pan the overflowing axis so
// the spot (percent of the painting) sits as near the middle as the edges
// allow. Returns the painting's size and offset in px. The W1 band is far
// wider than the painting on laptops (a strip), a phone band far narrower:
// either way the resident stays in frame.
export function coverPan(W, H, spot, aspect = HOME_ART_ASPECT) {
  if (!(W > 0) || !(H > 0)) return { pw: 0, ph: 0, tx: 0, ty: 0 };
  let pw = W; let ph = W / aspect;
  if (ph < H) { ph = H; pw = H * aspect; }
  const sx = spot ? spot.x / 100 : 0.5;
  const sy = spot ? spot.y / 100 : 0.5;
  const tx = Math.min(0, Math.max(W - pw, W / 2 - sx * pw));
  const ty = Math.min(0, Math.max(H - ph, H / 2 - sy * ph));
  return { pw, ph, tx, ty };
}

// The owner's hour → the painted light (§6 Clock + light; the numbers are
// the mockup's). n = the night painting's weight over the day one.
export function homeLight(h) {
  const hour = ((Number(h) % 24) + 24) % 24;
  const clamp01 = (v) => Math.max(0, Math.min(1, v));
  const n = (hour >= 20.5 || hour < 5) ? 1
    : hour >= 17 ? clamp01((hour - 17) / 3.5)
      : hour < 7 ? clamp01(1 - (hour - 5) / 2) : 0;
  const dusk = (hour >= 16 && hour < 21.5) ? clamp01(1 - Math.abs(hour - 18.7) / 2.5) : 0;
  const dawn = (hour >= 4.3 && hour < 8) ? clamp01(1 - Math.abs(hour - 6) / 1.7) : 0;
  // lamp pools: 0.35 + 0.5·n through dusk, 0.3 at full night, off by day
  const lamps = n >= 1 ? 0.3 : n > 0 ? 0.35 + 0.5 * n : 0;
  return { n, dusk, dawn, lamps };
}
// 🏡📐 Wave 3 (ruling 32): the frame is ONE card, so the columns under the
// caption (the chat lane | the journal rail) stand on the same ground the
// stage card paints: the home's, mixed day → night by the owner's hour; the
// light room's #120E18 before he has a home.
export const LIGHT_GROUND = '#120E18';
export function groundFor(homeKey, hour = 12) {
  const home = homeFor(homeKey);
  if (!home) return LIGHT_GROUND;
  const u = homeLight(hour).n;
  return `rgb(${home.ground.day.map((v, i) => Math.round(v + (home.ground.night[i] - v) * u)).join(',')})`;
}

// The light under him (§6 Mood light). Only real causes the page can SEE,
// each with its reason (the caption's mood line, and a tap on him).
// Precedence: a moment > storm > late > lamplight (§6); the newest moment
// wins and a moment decays (MOMENT_MS; a return after BACK_MS). W4: storm =
// the day's real weather (the house's almanac, the status `weather`) drawing
// rain on his glass: a storm, or steady rain.
export const MOODS = {
  lamplight: { rgb: '240,172,85', name: 'lamplight', why: null },
  back: { rgb: '255,199,104', name: 'warm gold', why: 'you came back' },
  made: { rgb: '238,150,174', name: 'rose', why: 'you two made something just now' },
  window: { rgb: '186,166,255', name: 'lilac', why: 'a window for you' },
  late: { rgb: '246,146,74', name: 'candlelight', why: 'late for you' },
  storm: { rgb: '150,180,226', name: 'storm blue', why: 'a storm outside' },
};
export const MOMENT_MS = 10 * 60 * 1000;
export const BACK_MS = 30 * 60 * 1000;
export const QUIET_MS = 60 * 60 * 1000;
const lateHour = (hour) => { const h = ((Number(hour) % 24) + 24) % 24; return h >= 23 || h < 4; };
export function moodFor({ hour, returning = false } = {}) {
  if (returning) return 'back';
  if (lateHour(hour)) return 'late';
  return 'lamplight';
}
// "after 3 days" · "after a day" · "after 9 hours" (a return is 6h+ by
// construction: the page's door effect only counts a real gap)
export function awayWords(ms) {
  const h = ms / 3600e3;
  if (!(h > 0)) return null;
  if (h >= 48) return `after ${Math.floor(h / 24)} days`;
  if (h >= 24) return 'after a day';
  return h >= 2 ? `after ${Math.floor(h)} hours` : null;
}
// When the viewer was last HERE: the later of this browser's previous load
// (the page's door stamp) and the viewer's own newest row before this visit
// (said or made from any device). A gap under 6 h is no return at all.
export function lastVisitBefore(results, { seatId = null, stamp = 0, returnedAt = 0 } = {}) {
  if (!stamp || !returnedAt) return 0;
  let mine = 0;
  for (const r of (results || [])) {
    for (const a of ((r && r.actions) || [])) {
      if (!a || !seatId) continue;
      const seat = a.game_instance_player_id || (a.data && a.data.sender_id) || null;
      if (seat !== seatId) continue;
      const t = Number(a.data && a.data.timestamp) || Date.parse(a.created_at || '') || 0;
      if (t && t < returnedAt && t > mine) mine = t;
    }
  }
  const since = Math.max(stamp, mine);
  return returnedAt - since >= 6 * 3600e3 ? since : 0;
}
export function moodState({
  nowMs = Date.now(), hour = 12, clockLabel = null, residentName = null, viewerIsOwner = false,
  awaySince = 0, returnedAt = 0, madeAt = 0, madeBy = null, madeKind = null, windowAt = 0, lastActivityAt = 0, loaded = true,
  weather = null,
} = {}) {
  const R = residentName || 'your resident';
  // a moment may land a little AHEAD of the caller's ticking clock (the stage
  // ticks every 30 s; clocks skew): up to a minute ahead counts, more is noise
  const fresh = (t, span) => Number.isFinite(t) && t > 0 && t - nowMs < 60000 && nowMs - t < span;
  const moments = [];
  if (fresh(madeAt, MOMENT_MS)) {
    // "you two made" only for something the owner MADE (a doodle, an
    // /imagine, a /build) while looking; his own = "Kenley made"; a plain
    // upload or a guest's doodle is just something new
    const why = madeBy === 'resident' ? `${R} made something just now`
      : (viewerIsOwner && madeBy === 'owner' && madeKind !== 'shared') ? 'you two made something just now' : 'something new just now';
    moments.push({ key: 'made', at: madeAt, why });
  }
  if (fresh(windowAt, MOMENT_MS)) moments.push({ key: 'window', at: windowAt, why: viewerIsOwner ? `${R} hung a window for you` : `${R} hung a window` });
  if (awaySince && fresh(returnedAt, BACK_MS)) {
    const w = awayWords(returnedAt - awaySince);
    moments.push({ key: 'back', at: returnedAt, why: w ? `you came back ${w}` : 'you came back' });
  }
  moments.sort((a, b) => b.at - a.at);
  const pick = (key, why) => ({ key, name: MOODS[key].name, rgb: MOODS[key].rgb, why });
  if (moments.length) return pick(moments[0].key, moments[0].why);
  const wx = weatherLook(weather, null);
  if (wx && wx.drops) return pick('storm', wx.kind === 'storm' ? 'a storm outside' : 'rain on the glass');
  if (lateHour(hour)) return pick('late', clockLabel ? `it's ${clockLabel} ${viewerIsOwner ? 'for you' : 'here'}` : 'late');
  // lamplight says "nothing new in the last hour" only when that is TRUE:
  // the room's rows have loaded and the newest is an hour old (or none exist)
  const quiet = loaded && (!(lastActivityAt > 0) || nowMs - lastActivityAt >= QUIET_MS);
  return pick('lamplight', quiet ? 'nothing new in the last hour' : null);
}

// The room's real signals, read from the rows the page already holds (zero
// queries): the newest thing anyone said, made or hung; the newest thing
// MADE (a picture / a doodle; never a reaction gif, never the room's own
// comics) and whose it was (the resident's, the owner's seat, or someone
// else's); the newest window. Normalized rows name the sender ONLY in
// data.sender_name; the seat rides game_instance_player_id (or sender_id).
const MADE_TYPES = new Set(['image', 'image_url', 'drawing']);
// MADE vs SHARED: a doodle, an /imagine /build /tinker is made; a plain upload
// (share-file stamps any picture 'image') is shared.
const MADE_COMMANDS = new Set(['imagine', 'build', 'tinker', 'combine']);
export function roomSignals(results, { residentName = null, ownerSeatId = null } = {}) {
  let lastActivityAt = 0; let madeAt = 0; let madeBy = null; let madeKind = null; let windowAt = 0; let rows = 0;
  const tsOf = (a) => Number(a && a.data && a.data.timestamp) || Date.parse((a && a.created_at) || '') || 0;
  for (const r of (results || [])) {
    for (const a of ((r && r.actions) || [])) {
      if (!a) continue;
      rows += 1;
      const d = a.data || {};
      const t = tsOf(a);
      if (!t) continue;
      // anything in the room's own feed is "new" (a /gift card, an announcement,
      // a whisper); the spectators' and the ghost lane's chatter is not the room
      if (a.type !== 'spectating_message' && !d.is_spectator && !d.is_ghost_chatter) lastActivityAt = Math.max(lastActivityAt, t);
      if (d.command === 'window') { windowAt = Math.max(windowAt, t); continue; }
      const roomComic = d.is_cutscene || d.is_arrival_comic || (!d.sender_id && d.sender_name === 'The Host');
      if (a.type === 'shared_content' && d.url && MADE_TYPES.has(d.content_type) && !d.is_reaction_gif && !roomComic && t > madeAt) {
        madeAt = t;
        const seat = a.game_instance_player_id || d.sender_id || null;
        madeBy = residentName && d.sender_name === residentName ? 'resident' : (ownerSeatId && seat === ownerSeatId ? 'owner' : 'other');
        madeKind = (d.content_type === 'drawing' || MADE_COMMANDS.has(d.command)) ? 'made' : 'shared';
      }
    }
  }
  return { lastActivityAt, madeAt, madeBy, madeKind, windowAt, loaded: rows > 0 };
}

// ── 🌦️ W4: the weather in the painted home (§6 "Weather") ──
// The status `weather` ({ kind, label }: the house's almanac on the owner's
// day, D6; absent when clear). What each kind draws, all client-side, $0:
// rain + storm = drops clipped to the window, and by day the home's painted
// STORM take (night beats weather: after dark the night painting stays and
// the rain runs down dark glass); storm adds lightning every 5-11 s; fog = a
// haze in the glass; snow = flakes; wind and leaves = a few leaves going by;
// heat = a warm haze on the glass (ruling 31). Dust and pollen draw nothing
// (an honest nothing beats a guess).
const WEATHER_DRAWN = {
  rain: { drops: true },
  storm: { drops: true, lightning: true },
  fog: { fog: true },
  snow: { snow: true },
  leaves: { leaves: true },
  heat: { heat: true },   // wave 2b (ruling 31 "DRAW HEAT TOO"): a warm haze on the glass
};
export function weatherLook(weather, L) {
  const kind = weather && typeof weather.kind === 'string' ? weather.kind : null;
  const d = kind ? WEATHER_DRAWN[kind] : null;
  if (!d) return null;
  const night = !!(L && L.n > 0.5);
  return {
    kind,
    label: String((weather && weather.label) || kind),
    drops: !!d.drops,
    lightning: !!d.lightning,
    fog: !!d.fog,
    snow: !!d.snow,
    leaves: !!d.leaves,
    heat: !!d.heat,                               // the haze (never overcast: the sky still shows)
    stormArt: !!d.drops && !night,                // night beats weather
    overcast: !!(d.drops || d.fog || d.snow),     // no clouds-in-blue, stars or moon behind it
  };
}
// The storm take is a painted repaint; a CSS stand-in covers a home whose take
// hasn't loaded (§6: "else a CSS stand-in").
export const STORM_STAND_IN = 'saturate(.5) brightness(.68) contrast(1.06) hue-rotate(-8deg)';
// Rain drops in the window, the same every render (seeded by the home's key).
export function rainField(home, n = 34) {
  if (!home) return [];
  let s = 2166136261;
  for (const ch of `${home.key}:rain`) s = Math.imul(s ^ ch.charCodeAt(0), 16777619) >>> 0;
  const rnd = () => { s = (s + 0x6D2B79F5) >>> 0; let t = s; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
  return Array.from({ length: n }, () => ({ left: rnd() * 100, delay: -rnd() * 1.2, dur: 0.55 + rnd() * 0.5, len: 7 + rnd() * 9 }));
}

// ── 🚪 W3: the painted front door ──
// "You've been gone 2 days." · "…9 hours." (a return is 6 h+ by construction)
export function goneWords(ms) {
  const h = ms / 3600e3;
  if (!(h >= 1)) return null;
  if (h >= 48) return `${Math.floor(h / 24)} days`;
  if (h >= 24) return 'a day';
  return h >= 2 ? `${Math.floor(h)} hours` : 'an hour';
}
// The door's printed lines (the app talking; names only, never a pronoun).
export function doorLines({ residentName = null, awaySince = 0, returnedAt = 0, deskNote = false } = {}) {
  const R = residentName || 'Your resident';
  const gone = awaySince && returnedAt ? goneWords(returnedAt - awaySince) : null;
  return {
    title: `${R} is home.`,
    sub: deskNote ? "There's something on the desk for you." : gone ? `You've been gone ${gone}.` : null,
  };
}
// The porch postcard (/explore): strangers see the outside only (§9). No
// owner name ever (the porch rule).
export function porchFor(homeKey) {
  const h = homeFor(homeKey);
  return h && h.outside ? { url: h.outside.url, line: h.outside.porch, focus: h.outside.door } : null;
}
// The porch's day count for strangers (ruling 31, E4): "12 days alive" from
// the port day (`porch.day_n`), never "DAY N TOGETHER" (that count is the
// owner's). null when there is no count.
export function porchDaysAlive(dayN) {
  const n = Number(dayN);
  if (!Number.isFinite(n) || n < 1) return null;
  const d = Math.floor(n);
  return `${d} ${d === 1 ? 'day' : 'days'} alive`;
}

// ── the window sky (W2d; the mockup's layers, §6 "Window sky") ──
// The moon's box inside the window (% of the window). The Harvest Moon is
// drawn 1.35x and warm (the name comes from portedPlace.moonInfo).
export function moonBox(home, { harvest = false } = {}) {
  if (!home || !home.moon) return null;
  const w = home.win; const m = home.moon;
  return { left: ((m.x - w.x) / w.w) * 100, top: ((m.y - w.y) / w.h) * 100, width: ((m.size * (harvest ? 1.35 : 1)) / w.w) * 100 };
}
// How much of each sky layer shows at this light (glass-walled homes: the
// moon only). Night weight n from homeLight.
export function skyLayers(home, L, wx = null) {
  const clamp01 = (v) => Math.max(0, Math.min(1, v));
  if (!home || !L) return { clouds: 0, stars: 0, moon: 0 };
  if (wx && wx.overcast) return { clouds: 0, stars: 0, moon: 0 };   // W4: nothing shows through a storm, fog or snow
  return {
    clouds: home.sky.clouds ? (1 - L.n) * (1 - L.dusk * 0.6) : 0,
    stars: home.sky.stars ? clamp01((L.n - 0.35) / 0.65) : 0,
    moon: clamp01((L.n - 0.3) / 0.6),
  };
}
// Stars in the window, the same every render (seeded by the home's key).
export function starField(home, n = 26) {
  if (!home) return [];
  let s = 2166136261;
  for (const ch of home.key) s = Math.imul(s ^ ch.charCodeAt(0), 16777619) >>> 0;
  const rnd = () => { s = (s + 0x6D2B79F5) >>> 0; let t = s; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
  return Array.from({ length: n }, () => ({ left: 4 + rnd() * 92, top: 4 + rnd() * Math.max(10, home.starsBelow - 10), delay: -rnd() * 3.4 }));
}

// ── the cork board (W2d): the newest made things, pinned ──
// Slots are the mockup's (% of the board, rotation); the board holds the
// newest BOARD_MAX, oldest first so a pin keeps its slot while the board fills.
// W2c: the owner's pinned lines of his (utils/portedPlace pinnedQuotes, kind
// 'quote') take the RESERVED tail slots 7..9, newest QUOTE_MAX, so a pin never
// moves a made thing. The board is an ALLOW-LIST and guests see it: a note of
// the owner's (kind 'note', private) can never reach it, whatever it is handed.
export const BOARD_SLOTS = [[4, 3, -7], [56, 1, 5], [26, 33, -3], [62, 38, 8], [3, 63, 4], [36, 67, -6], [66, 72, 3], [18, 18, 6], [46, 52, -4], [10, 44, -2], [72, 16, 6], [50, 84, -3]];
export const BOARD_MAX = 7;
export const QUOTE_MAX = 3;
// Wave 3 (ruling 32): his PUBLIC keepsakes hang too: the favorites of the
// week (utils/portedPlace favoritePins, shaped from the status ported block's
// `favorites`, which every viewer gets) take the last two slots, 10..11.
export const FAV_MAX = 2;
// W3: the arrival comic is the FIRST pin (idea bank: "the arrival comic starts
// Keepsakes AND is the first pin on the board"): it keeps slot 0 for good and
// the newest made things take the rest, so it never falls off the board.
export function boardPins(keepsakes, quotes = [], favorites = []) {
  const pool = [...(keepsakes || [])].filter((k) => k && (k.kind === 'photo' || k.kind === 'window'));
  const arrival = pool.filter((k) => k.arrival).sort((a, b) => a.ts - b.ts)[0] || null;
  const newest = pool.filter((k) => k !== arrival).sort((a, b) => b.ts - a.ts).slice(0, BOARD_MAX - (arrival ? 1 : 0)).reverse();
  const made = (arrival ? [arrival, ...newest] : newest).map((k, i) => ({ ...k, slot: BOARD_SLOTS[i % BOARD_SLOTS.length] }));
  const lines = (Array.isArray(quotes) ? quotes : [])
    .filter((q) => q && q.kind === 'quote' && String(q.id || '').startsWith('pin:') && String(q.text || '').trim())
    .sort((a, b) => (b.ts || 0) - (a.ts || 0)).slice(0, QUOTE_MAX).reverse();
  // (an allow-list like the quotes: only a SHAPED favorite, never a raw status row or a note)
  const favs = (Array.isArray(favorites) ? favorites : [])
    .filter((f) => f && f.kind === 'fav' && String(f.id || '').startsWith('fav:') && String(f.text || '').trim())
    .sort((a, b) => (b.ts || 0) - (a.ts || 0)).slice(0, FAV_MAX).reverse();
  return made.concat(lines.map((q, j) => ({ ...q, slot: BOARD_SLOTS[BOARD_MAX + j] })))
    .concat(favs.map((f, j) => ({ ...f, slot: BOARD_SLOTS[BOARD_MAX + QUOTE_MAX + j] })));
}

// ── 📐 the stage on a laptop (§0-ter #4; Nina: "shrinks as you chat") ──
// At rest the WHOLE painting (its own shape), capped so the chat below keeps
// its newest lines + the composer; a strip while you chat. The frame (the
// stage, the caption, chat + journal) is as wide as the resting painting,
// never narrower than minW (a short screen crops the painting a little
// rather than squeezing the chat), never wider than the screen or maxW.
export const STAGE_STRIP = 168;
// 🏡📐 Wave 3 (ruling 32, 09-26): the PAGE SCROLLS. With `scroll` the resting
// painting is never capped by the screen (its own shape, the frame as wide as
// the mockup's 1040), and under it the chat lane and the journal get their
// full height: LANE_MIN (the mockup's ~583px: five lines + the whole page) or
// what fills the screen in the strip state, whichever is more. The bar on the
// frame is BAR_H tall (the mockup's 37).
export const LANE_MIN = 583;
export const BAR_H = 37;
export function placeFrame({ viewW = 1280, viewH = 800, chrome = 0, minChat = 220, pad = 20, minW = 960, maxW = 1240, aspect = HOME_ART_ASPECT, scroll = false } = {}) {
  const fitW = Math.max(0, Math.min(viewW - 2 * pad, maxW));
  const budget = scroll ? Infinity : Math.max(STAGE_STRIP, viewH - chrome - minChat);
  const restH = Math.round(Math.max(STAGE_STRIP, Math.min(fitW / aspect, budget)));
  // (scroll: the frame is exactly what fits, the painting follows its shape)
  const frameW = scroll ? Math.round(fitW) : Math.round(Math.min(fitW, Math.max(Math.min(minW, fitW), restH * aspect)));
  return { frameW, restH, stripH: Math.min(STAGE_STRIP, restH) };
}

// Which homes the owner's scene opens as doors, in order (the server's
// shortlist; the painted ones only).
export const doorsFor = (shortlist) => (Array.isArray(shortlist) ? shortlist : [])
  .map(homeFor).filter(Boolean).slice(0, 3);

// ── the owner's scene, as pure state (§5c; jest pins it) ──
// light:  the light room (the "find a place" button may show)
// doors:  the owner pressed, or the server says 'asked': doors up, waiting
//         for his REAL turn (an ask older than ASK_STALE_MS is let go: the
//         button returns, and his answer still lands whenever it comes)
// reveal: his turn chose and this device hasn't played it yet: his lines,
//         then through the door (a choice made while the owner was away
//         plays when they come back)
// home:   the painted home. Guests go straight from light to home: the
//         scenes are the owner's.
export const ASK_STALE_MS = 6 * 60 * 60 * 1000;
export function askLive(choice, nowMs = Date.now()) {
  if (!choice || choice.state !== 'asked') return false;
  const at = Date.parse(choice.asked_at || '');
  return !Number.isFinite(at) || nowMs - at < ASK_STALE_MS;
}
// 🧳 W3 Move house: he has a home and the owner asked him to choose again:
// the owner's stage goes back to the doors (the light room) until his turn
// lands; guests keep the home they see (never the scene).
export function stagePhase({ homeKey = null, choice = null, isOwner = false, revealSeen = false, pressed = false, nowMs = Date.now() } = {}) {
  if (homeKey) {
    if (isOwner && (pressed || (choice && choice.move && askLive(choice, nowMs)))) return 'doors';
    return (!isOwner || revealSeen) ? 'home' : 'reveal';
  }
  if (!isOwner) return 'light';
  if (pressed || askLive(choice, nowMs)) return 'doors';
  return 'light';
}
export function showFindButton({ homeKey = null, choice = null, isOwner = false, pressed = false, exchanged = false, nowMs = Date.now() } = {}) {
  return !!isOwner && !homeKey && !!exchanged && !pressed && !askLive(choice, nowMs);
}
// "After the first real chat": the owner said something, then he answered.
// Normalized rows name the sender ONLY in data.sender_name; history rows count.
export function hasExchange(results, { residentName = null, ownerSeatId = null } = {}) {
  if (!residentName || !ownerSeatId) return false;
  let ownerAt = null;
  const rows = [];
  for (const r of (results || [])) for (const a of (r.actions || [])) rows.push(a);
  const ts = (a) => Date.parse(a.created_at || '') || Number(a.data && a.data.timestamp) || 0;
  rows.sort((x, y) => ts(x) - ts(y));
  for (const a of rows) {
    if (!a || a.type !== 'public_message') continue;
    const seat = a.game_instance_player_id || (a.data && a.data.sender_id) || null;
    if (seat === ownerSeatId && ownerAt == null) { ownerAt = ts(a); continue; }
    if (ownerAt != null && a.data && a.data.sender_name === residentName && !a.data.is_arrival && ts(a) >= ownerAt) return true;
  }
  return false;
}
export const revealSeenKey = (gameId) => `ported_home_reveal_${gameId}`;
// What this device stores once it has played a reveal. A first choice stores
// the home's key (the W2a shape: old devices keep working); a MOVE stores the
// key AND when he chose, so moving (or staying) plays its own reveal.
export function revealStamp(homeKey, choice) {
  if (!homeKey) return '';
  return choice && choice.move && choice.state === 'chosen' && choice.chosen_at ? `${homeKey}@${choice.chosen_at}` : homeKey;
}
// 🧳 The owner may ask him to move once he lives somewhere, no ask is live,
// and he didn't just choose (once a DAY, ruling 31 09-26: the server's own
// day; its answer is the truth).
export const MOVE_COOLDOWN_MS = 24 * 60 * 60 * 1000;
export function canMove({ homeKey = null, choice = null, isOwner = false, nowMs = Date.now() } = {}) {
  if (!isOwner || !homeKey) return false;
  if (askLive(choice, nowMs)) return false;
  const at = Date.parse((choice && choice.chosen_at) || '');
  return !(Number.isFinite(at) && nowMs - at < MOVE_COOLDOWN_MS);
}
